import { z } from 'zod/v4';

// Beware that origin for parts is different from origin for offers!
export enum OTSPartOriginEnum {
    Manual = 'Manual',
    System = 'System',
    Import = 'Import',
    Aggregate = 'Aggregate',
    DescriptionExtraction = 'DescriptionExtraction',
    SupplierPortal = 'SupplierPortal',
    // ----
    AlfatecAPI = 'AlfatecAPI',
    ArrowAPI = 'ArrowAPI',
    AvnetAPI = 'AvnetAPI',
    AvnetUsaAPI = 'AvnetUsaAPI',
    BuerklinAPI = 'BuerklinAPI',
    BossardAPI = 'BossardAPI',
    ChipAssistAPI = 'ChipAssistAPI',
    DigikeyAPI = 'DigikeyAPI',
    Element14API = 'Element14API',
    EveAPI = 'EveAPI',
    FarnellAPI = 'FarnellAPI',
    FutureAPI = 'FutureAPI',
    GudecoAPI = 'GudecoAPI',
    HeilindAPI = 'HeilindAPI',
    IHSImport = 'IHSImport',
    MasterAPI = 'MasterAPI',
    MouserAPI = 'MouserAPI',
    MyArrowAPI = 'MyArrowAPI',
    LcscAPI = 'LcscAPI',
    NewarkAPI = 'NewarkAPI',
    OctopartAPI = 'OctopartAPI',
    OnlineComponentsAPI = 'OnlineComponentsAPI',
    RapidElectronicsAPI = 'RapidElectronicsAPI',
    RsComponentsAPI = 'RsComponentsAPI',
    RmComponentsAPI = 'RmComponentsAPI',
    RochesterAPI = 'RochesterAPI',
    QuestComponentsAPI = 'QuestComponentsAPI',
    RutronikAPI = 'RutronikAPI',
    SchukatAPI = 'SchukatAPI',
    SiliconExpertAPI = 'SiliconExpertAPI',
    SluiceboxAPI = 'SluiceboxAPI',
    SosAPI = 'SosAPI',
    EcomalAPI = 'EcomalAPI',
    SourcengineAPI = 'SourcengineAPI',
    TelconaAPI = 'TelconaAPI',
    TiAPI = 'TiAPI',
    TtiAPI = 'TtiAPI',
    TmeAPI = 'TmeAPI',
    TrustedPartsAPI = 'TrustedPartsAPI',
    VenkelAPI = 'VenkelAPI',
    WeltronAPI = 'WeltronAPI',
    WuerthEisosAPI = 'WuerthEisosAPI',
    CorestaffAPI = 'CorestaffAPI',
    BlumeAPI = 'BlumeAPI',
    SamtecAPI = 'SamtecAPI',
    AlElektronikAPI = 'AlElektronikAPI',
    AvnetApacAPI = 'AvnetApacAPI',
    ChipCartAPI = 'ChipCartAPI',
    WinSourceAPI = 'WinSourceAPI',
    IcHubAPI = 'IcHubAPI',
    UnikeyAPI = 'UnikeyAPI',
    GreenchipsAPI = 'GreenchipsAPI',
    WittigAPI = 'WittigAPI',
    HexachipAPI = 'HexachipAPI',
    VPL = 'VPL',
    Supplyframe = 'Supplyframe',
    KruseAPI = 'KruseAPI',
    IneltroAPI = 'IneltroAPI',
    AtlantikNetworxxAPI = 'AtlantikNetworxxAPI',
    MevAPI = 'MevAPI',
}

const OTSPartOriginEnumRuntype = z.enum(OTSPartOriginEnum);
// Keep in sync with the backend's `OriginEnum`.
const OTSPartOriginWithoutDataEnumRuntype = OTSPartOriginEnumRuntype.exclude([
    OTSPartOriginEnum.SiliconExpertAPI,
    OTSPartOriginEnum.OctopartAPI,
    OTSPartOriginEnum.IHSImport,
    OTSPartOriginEnum.Import,
    OTSPartOriginEnum.DescriptionExtraction,
    OTSPartOriginEnum.VPL,
    OTSPartOriginEnum.Supplyframe,
    OTSPartOriginEnum.SupplierPortal,
]);

const OctopartOriginDataRuntype = z.object({
    part_id: z.string(),
    manufacturer: z.object({
        manufacturer_id: z.string(),
        manufacturer_name: z.string(),
    }),
});

const IHSOriginDataRuntype = z.object({
    part_number: z.string(),
    manufacturer: z.object({
        short_manufacturer_name: z.string(),
        manufacturer_name: z.string(),
    }),
});

const SiliconExpertIdRuntype = z.object({
    se_id: z.string(),
});

export enum SiliconExpertDataOriginEnum {
    PartSearch = 'PartSearch',
    PartDetail = 'PartDetail',
}

export type SiliconExpertOrigin = z.infer<typeof SiliconExpertOriginRuntype>;
const SiliconExpertOriginRuntype = z.object({
    type: z.literal(OTSPartOriginEnum.SiliconExpertAPI),
    data: z
        .object({
            com_id: z.string(),
            mpn: z.string(),
            manufacturer: z.string(),
            origin: z.enum(SiliconExpertDataOriginEnum),
        })
        .nullable(),
});

const DescriptionExtractionDataRuntype = z.object({
    original_vote: z.string(),
    extraction_version: z.number(),
    origin: z.object({
        type: OTSPartOriginEnumRuntype,
    }),
});

const VPLOriginDataRuntype = z.object({
    mpn: z.string(),
    manufacturer: z.string(),
    vpl_package: z.string().nullable(),
});

const SupplyframeOriginDataRuntype = z.object({
    mpn: z.string(),
    manufacturer: z.string(),
    pins: z.uint32().nullable(),
    package: z.string().nullable(),
    mounting: z.string().nullable(),
});

export type PartOrigin = z.infer<typeof PartOriginRuntype>;
export const PartOriginRuntype = z.discriminatedUnion('type', [
    SiliconExpertOriginRuntype,
    z.object({
        type: z.literal(OTSPartOriginEnum.OctopartAPI),
        data: OctopartOriginDataRuntype.nullable(),
    }),
    z.object({
        type: z.literal(OTSPartOriginEnum.IHSImport),
        data: IHSOriginDataRuntype.nullable(),
    }),
    z.object({
        type: z.literal(OTSPartOriginEnum.Import),
        data: SiliconExpertIdRuntype.nullable(),
    }),
    z.object({
        type: z.literal(OTSPartOriginEnum.DescriptionExtraction),
        data: DescriptionExtractionDataRuntype.nullable(),
    }),
    z.object({
        type: z.literal(OTSPartOriginEnum.VPL),
        data: VPLOriginDataRuntype.nullable(),
    }),
    z.object({
        type: z.literal(OTSPartOriginEnum.Supplyframe),
        data: SupplyframeOriginDataRuntype.nullable(),
    }),
    z.object({
        type: z.literal(OTSPartOriginEnum.SupplierPortal),
        data: z.uuid().nullable(),
    }),
    z.object({
        type: OTSPartOriginWithoutDataEnumRuntype,
        data: z.undefined().optional(),
    }),
]);

export function isSiliconExpertOrigin(origin: PartOrigin): origin is SiliconExpertOrigin {
    return origin.type === OTSPartOriginEnum.SiliconExpertAPI;
}
